/**
 * /job/{ticketNumber}/summary — what a scan of a finished job's label shows:
 * the device was collected, the job is closed or deleted, or it was resolved
 * longer ago than the pickup window (30 days) with no collection recorded.
 * Offers a pre-filled Return check-in (the customer is back with it), Go to
 * pickup (it really is still on the shelf), and undoing a collection (a
 * mis-scan), which any staff member may do; the undo appends an audit row
 * rather than erasing the collection
 * (docs/qr-scan-workflow-requirements.md §9, rule 3 + D16).
 *
 * Mobile-first, mounted outside MainLayout like /pickup.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { uncollectWorkItem } from '../services/workItemDataService';
import { useWorkItemByNumber } from '../hooks/useWorkItemByNumber';
import { formatSydneyDateTime } from '../utils/sydneyTime';
import { formatStatusDisplay, normalizeStatus } from '../components/workitems/utils/workItemUtils';
import './MobilePickupPage.css';

export function MobileJobSummaryPage(): React.ReactElement {
  const { ticketNumber = '' } = useParams<{ ticketNumber: string }>();
  const navigate = useNavigate();
  const { item, loading, error, reload } = useWorkItemByNumber(ticketNumber);
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoReason, setUndoReason] = useState('');
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);

  const handleUndo = async () => {
    if (!item) return;
    setUndoing(true);
    setUndoError(null);
    try {
      await uncollectWorkItem(item.guid, { reason: undoReason.trim() || undefined });
      setUndoOpen(false);
      setUndoReason('');
      await reload();
    } catch (e) {
      setUndoError(e instanceof Error ? e.message : 'Failed to undo collection');
    } finally {
      setUndoing(false);
    }
  };

  if (loading) {
    return (
      <div className="pickup-page pickup-center">
        <CircularProgress />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="pickup-page">
        <Alert severity="error">{error ?? 'Could not load this job.'}</Alert>
      </div>
    );
  }

  const status = item.jobStatus || item.status || '';
  const slug = normalizeStatus(status);
  const isDeleted = slug === 'deleted';
  const isResolved = slug === 'resolved';
  // Mirrors RETURN_ELIGIBLE_STATUSES on the ticket page.
  const canReturn = item.number != null && ['resolved', 'closed'].includes(slug);
  const customer = item.customerBusinessName || item.customerName || 'Customer';

  return (
    <div className="pickup-page">
      <header className="pickup-header">
        <div className="pickup-ticket">#{item.number ?? ticketNumber}</div>
        <div className="pickup-customer">{customer}</div>
        {item.title && <div className="pickup-muted">{item.title}</div>}
        {status && <span className="pickup-status">{formatStatusDisplay(status)}</span>}
      </header>

      <Stack spacing={2}>
        {item.collectedAt ? (
          <section className="pickup-card">
            <div className="pickup-balance-label">Collected</div>
            <div className="pickup-done-title">{formatSydneyDateTime(item.collectedAt)}</div>
            {item.collectedBy && <p className="pickup-muted">Handed over by {item.collectedBy}</p>}
            {item.collectionNote && <p>{item.collectionNote}</p>}
            <Button variant="text" color="warning" onClick={() => setUndoOpen(true)}>
              Undo collection
            </Button>
          </section>
        ) : isDeleted ? (
          <Alert severity="warning">This job was deleted.</Alert>
        ) : (
          <Alert severity="info">
            {isResolved && item.resolvedAt
              ? `Finished ${formatSydneyDateTime(item.resolvedAt)} — collection not recorded.`
              : `This job is ${status ? formatStatusDisplay(status).toLowerCase() : 'finished'} and has not been marked collected.`}
          </Alert>
        )}

        {canReturn && (
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(`/check-in/new?return=${item.number}`)}
          >
            Return check-in
          </Button>
        )}

        {!item.collectedAt && isResolved && (
          <Button variant="outlined" size="large" onClick={() => navigate(`/pickup/${item.number}`)}>
            Still here — go to pickup
          </Button>
        )}

        <Button variant="outlined" size="large" onClick={() => navigate(`/ticket/${item.number ?? ticketNumber}`)}>
          Full view
        </Button>
      </Stack>

      <Dialog open={undoOpen} onClose={() => !undoing && setUndoOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Undo collection?</DialogTitle>
        <DialogContent>
          <p className="pickup-muted">
            The job goes back to not collected. The original collection stays on its record.
          </p>
          <TextField
            label="Reason (optional)"
            value={undoReason}
            onChange={(e) => setUndoReason(e.target.value)}
            placeholder="e.g. Scanned the wrong label"
            fullWidth
            disabled={undoing}
            inputProps={{ maxLength: 4000 }}
          />
          {undoError && <Alert severity="error" sx={{ mt: 2 }}>{undoError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoOpen(false)} disabled={undoing}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleUndo} disabled={undoing}>
            {undoing ? 'Undoing…' : 'Undo collection'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MobileJobSummaryPage;
